/**
 * Composant principal de l'onglet Paiement d'un chantier
 * Refonte complète avec gestion des paiements individuels, ajustements et OCR
 */

import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Loader2, Wallet } from 'lucide-react';
import { usePaiementsChantier } from '@/hooks/usePaiementsChantier';
import { PaiementKPIs } from './PaiementKPIs';
import { StatutPaiementBadge } from './StatutPaiementBadge';
import { AjustementsSection } from './AjustementsSection';
import { PaiementsList } from './PaiementsList';
import { PaiementFormModal } from './PaiementFormModal';
import { AjustementFormModal } from './AjustementFormModal';
import type { PaiementClient as PaiementClientType, PaiementFormData, TypeAjustement } from '@/types/paiement';


interface PaiementClientProps {
  chantierId: string;
  montantDevisHT: number;
  montantDevisTTC?: number;
  onUpdate?: () => void;
  /** Indique si le composant est dans le flow guidé (pour le highlight du bouton) */
  isGuidedFlow?: boolean;
}

export function PaiementClient({
  chantierId,
  montantDevisHT,
  montantDevisTTC,
  onUpdate,
  isGuidedFlow = false
}: PaiementClientProps) {
  // Hook pour gérer les paiements
  const {
    paiements,
    ajustements,
    totals,
    facturesClients,
    isLoading,
    error,
    addPaiement,
    updatePaiement,
    deletePaiement,
    addAjustement,
    deleteAjustement,
  } = usePaiementsChantier(chantierId);

  // État de la modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [paiementEnEdition, setPaiementEnEdition] = useState<PaiementClientType | null>(null);

  // État de la modal ajustement (avenant/remise)
  const [ajustementModalType, setAjustementModalType] = useState<TypeAjustement | null>(null);

  // Ouvrir la modal pour ajouter
  const handleAdd = () => {
    setPaiementEnEdition(null);
    setIsModalOpen(true);
  };

  // Ouvrir la modal pour modifier
  const handleEdit = (paiement: PaiementClientType) => {
    setPaiementEnEdition(paiement);
    setIsModalOpen(true);
  };

  // Soumettre le formulaire (ajout ou modification)
  const handleSubmit = async (data: PaiementFormData) => {
    let result;
    if (paiementEnEdition) {
      const success = await updatePaiement(paiementEnEdition.id, data);
      result = success ? { ...paiementEnEdition, ...data } : null;
    } else {
      result = await addPaiement(data);
    }

    if (result && onUpdate) {
      onUpdate();
    }

    return result as PaiementClientType | null;
  };

  // Fermer la modal
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setPaiementEnEdition(null);
  };

  // Supprimer un paiement avec callback
  const handleDelete = async (id: string): Promise<boolean> => {
    const success = await deletePaiement(id);
    if (success && onUpdate) {
      onUpdate();
    }
    return success;
  };

  // État de chargement
  if (isLoading) {
    return (
      <Card className="bg-card border-border">
        <CardContent className="p-6 flex items-center justify-center min-h-[300px]">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  // État d'erreur
  if (error) {
    return (
      <Card className="bg-card border-border">
        <CardContent className="p-6">
          <div className="text-center text-destructive">
            <p>Erreur lors du chargement des paiements</p>
            <p className="text-sm text-muted-foreground mt-2">{error.message}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-card border-border">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-xl">
            <Wallet className="h-5 w-5" />
            Paiement client
          </CardTitle>
          <StatutPaiementBadge
            statut={totals?.statut_paiement}
            size="md"
            showIcon
          />
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* KPIs */}
        <PaiementKPIs
          totals={totals}
          montantDevisHT={montantDevisHT}
        />

        {/* Sections avenants et remises — masquées tant qu'il n'y en a pas */}
        {ajustements.length > 0 && (
          <div className="space-y-2">
            {ajustements.some(a => a.type === 'avenant') && (
              <AjustementsSection
                type="avenant"
                ajustements={ajustements}
                onDelete={deleteAjustement}
              />
            )}
            {ajustements.some(a => a.type === 'remise') && (
              <AjustementsSection
                type="remise"
                ajustements={ajustements}
                onDelete={deleteAjustement}
              />
            )}
          </div>
        )}

        <Separator />

        {/* Liste des paiements */}
        <PaiementsList
          paiements={paiements}
          facturesClients={facturesClients}
          onAdd={handleAdd}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onAddAvenant={() => setAjustementModalType('avenant')}
          onAddRemise={() => setAjustementModalType('remise')}
        />

        {/* Modal d'ajout/édition paiement */}
        <PaiementFormModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          onSubmit={handleSubmit}
          paiement={paiementEnEdition}
          facturesClients={facturesClients}
          totalDevis={totals?.total_devis || montantDevisHT}
          resteAEncaisser={totals?.reste_a_encaisser || 0}
        />

        {/* Modal d'ajout ajustement (avenant/remise) */}
        {ajustementModalType && (
          <AjustementFormModal
            isOpen={true}
            onClose={() => setAjustementModalType(null)}
            type={ajustementModalType}
            onSubmit={addAjustement}
          />
        )}
      </CardContent>
    </Card>
  );
}

export default PaiementClient;
